// When the middlegame turns (2026-10-10): the score the reader's own
// middlegame moves gave up, by move-number band. Read from the middlegame's
// curve (`GameFacts.stageCurves`), so it needs no fact of its own. The
// Middlegame screen's "When it turns" card averages it over the middlegames
// that played moves in each band (`middlegameTurnsPlayed`), and the
// library's `middlegame-turn` filter (resolved by the store's
// `resolveFilter`) opens the games behind each band: both use the rule here,
// so a band's count and the filtered library's are the same number.
import type { Color } from './chess.ts';
import type { GameFacts } from './facts.ts';
import { SLIGHT_CHANGE_PAWNS } from './classification.ts';

/**
 * A band of move numbers (full moves: ply 23 is move 12): up to 15, then
 * five moves each, and 36 on. Most middlegames start between moves 8 and 15
 * and few run past 40, so the first band holds the early middlegame and the
 * last its long tail.
 */
export type MiddlegameTurn = 'to-15' | '16-20' | '21-25' | '26-30' | '31-35' | '36-on';
export const MIDDLEGAME_TURNS = ['to-15', '16-20', '21-25', '26-30', '31-35', '36-on'] as const satisfies readonly MiddlegameTurn[];

/** The band's words, as a chart's axis and the filter's options write them: "–15", "16–20", "36+". */
export const MIDDLEGAME_TURN_LABEL: Record<MiddlegameTurn, string> = {
  'to-15': '–15',
  '16-20': '16–20',
  '21-25': '21–25',
  '26-30': '26–30',
  '31-35': '31–35',
  '36-on': '36+',
};

/** A game turns in a band when the reader's moves there gave up at least this many pawns in all: the score-change bands' "slightly". */
export const MIDDLEGAME_TURN_PAWNS = SLIGHT_CHANGE_PAWNS;

/** The band of a move number (1-based full moves). */
export function middlegameTurnOf(move: number): MiddlegameTurn {
  if (move <= 15) return 'to-15';
  if (move >= 36) return '36-on';
  return MIDDLEGAME_TURNS[Math.floor((move - 16) / 5) + 1]!;
}

/** Whether a ply (1-based) is the reader's move: white plays the odd plies. */
const readers = (ply: number, colour: Color) => (ply % 2 === 1) === (colour === 'white');

/**
 * Pawns the reader's own middlegame moves gave up, by band, each a positive
 * number: for every reader move with a score before and after it, what the
 * score fell by (a rise gives nothing back). Bands the reader lost nothing in
 * are absent; so is everything for a game that is not scanned or never
 * reached a middlegame.
 */
export function middlegameLossByTurn(f: Pick<GameFacts, 'colour' | 'scanned' | 'stageCurves'>): Partial<Record<MiddlegameTurn, number>> {
  const curve = f.scanned ? f.stageCurves?.middlegame : undefined;
  const out: Partial<Record<MiddlegameTurn, number>> = {};
  if (!curve) return out;
  for (let ply = curve.fromPly + 1; ply <= curve.toPly; ply++) {
    if (!readers(ply, f.colour)) continue;
    const before = curve.scores[ply - 1 - curve.fromPly];
    const after = curve.scores[ply - curve.fromPly];
    if (before == null || after == null || after >= before) continue;
    const band = middlegameTurnOf(Math.ceil(ply / 2));
    out[band] = (out[band] ?? 0) + (before - after);
  }
  // To the hundredth of a pawn, so a sum like 0.3 + 0.2 meets the threshold.
  for (const band of Object.keys(out) as MiddlegameTurn[]) out[band] = Math.round(out[band]! * 100) / 100;
  return out;
}

/**
 * The bands a game's reader played middlegame moves in: at least one of their
 * moves there with a score before and after it (the moves
 * `middlegameLossByTurn` reads). Empty for a game that is not scanned or never
 * reached a middlegame. The "When it turns" card divides a band's loss by the
 * games that played there, so a band many games reach and one few reach read
 * on the same scale.
 */
export function middlegameTurnsPlayed(f: Pick<GameFacts, 'colour' | 'scanned' | 'stageCurves'>): MiddlegameTurn[] {
  const curve = f.scanned ? f.stageCurves?.middlegame : undefined;
  const played = new Set<MiddlegameTurn>();
  if (!curve) return [];
  for (let ply = curve.fromPly + 1; ply <= curve.toPly; ply++) {
    if (!readers(ply, f.colour)) continue;
    if (curve.scores[ply - 1 - curve.fromPly] == null || curve.scores[ply - curve.fromPly] == null) continue;
    played.add(middlegameTurnOf(Math.ceil(ply / 2)));
  }
  return MIDDLEGAME_TURNS.filter((band) => played.has(band));
}

/** The bands a game turns in: where its reader's moves gave up at least `MIDDLEGAME_TURN_PAWNS`. */
export function middlegameTurnsOf(f: Pick<GameFacts, 'colour' | 'scanned' | 'stageCurves'>): MiddlegameTurn[] {
  const loss = middlegameLossByTurn(f);
  return MIDDLEGAME_TURNS.filter((band) => (loss[band] ?? 0) >= MIDDLEGAME_TURN_PAWNS);
}
